import {
  Button,
  FormHelperText,
  Grid,
  Typography,
  makeStyles
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { ErrorMessage, useFormikContext } from 'formik';
import { inject, observer } from 'mobx-react';
import { withSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../../components/CustomDialog';
import DocumentPreview from '../../../components/DocumentPreview';
import AutocompleteWithFormik from '../../../components/inputs/AutocompleteWithFormik';
import MonedaService from '../../../services/MonedaService';
import DataTable from '../../../toolkit/DataTable/DataTable';
import ListDataProvider from '../../../toolkit/mobx-connectors/ListDataProvider';
import { facturaDefault } from '../../../utils/data-utils';
import { formatNumber } from '../../../utils/form-utils';
import FacturasAddForm from './FacturasAddForm';
import facturasConfig from './FacturasConfig';

const useStyles = makeStyles(theme => ({
  container: {
    paddingBottom: theme.spacing(8),
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 0,
      padding: 0
    }
  },
  img: {
    width: '150px',
    [theme.breakpoints.down('sm')]: {
      width: '100px'
    }
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short
    })
  },
  expandOpen: {
    transform: 'rotate(180deg)'
  },
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(4),
    marginLeft: theme.spacing(6),
    marginRight: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      marginBottom: theme.spacing(5),
      padding: theme.spacing(2),
      paddingTop: theme.spacing(4)
    }
  },
  tableCellAction: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  button: {
    margin: 0,
    textDecoration: 'none',
    marginLeft: theme.spacing(1)
  },
  addButon: {
    padding: '15px'
  },
  leftIcon: {
    marginRight: theme.spacing(1)
  },
  rightIcon: {
    marginLeft: theme.spacing(1)
  },
  iconSmall: {
    fontSize: 20
  },

  toolbar: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  cardStyle: {
    borderRadius: '4px 4px 0px 0px'
  },
  tableContainerItem: {
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(3)
  }
}));

const FacturasForm = props => {
  const { gridProps } = props;
  const { enqueueSnackbar } = props;
  const classes = useStyles();
  const [facturaConfig, setFacturaConfig] = useState({ open: false });
  const [facturaConfirmConfig, setConfirmConfig] = useState({ open: false });
  const [monedas, setMonedas] = useState([]);
  const { t } = useTranslation();
  const formik = useFormikContext();
  const [pdfPreview, setPdfPreview] = useState();

  const onPdfView = value => {
    const file = value.factura[0];
    const url = URL.createObjectURL(file);
    setPdfPreview({ url, title: file.name });
  };

  const onEdit = value => {
    setFacturaConfig({ defaultValue: value, open: true, isEdit: true });
  };

  const onDelete = value => {
    // Se actualizan las facturas
    const newArray = [...formik.values.facturas];
    const index = newArray.findIndex(item => item.id === value.id);
    newArray.splice(index, 1);
    formik.setFieldValue(`facturas`, newArray);

    // Se actualizan la mercaderías que usaban esa factura
    const newArrayMercaderias = [...formik.values.mercaderias];
    const mercaderias = newArrayMercaderias.map(item => {
      if (item.factura.id === value.id) {
        return { ...item, factura: { id: '' } };
      }
      return item;
    });
    formik.setFieldValue(`mercaderias`, mercaderias);

    props.datos.list.resetListData();
    props.datos.list.dataLoaded(newArray, {
      currentPage: 0,
      pageSize: 10,
      itemsCount: newArray.length
    });
  };

  const onDeletePre = value => {
    if (value.id) {
      const usados = formik.values?.mercaderias?.filter(
        item => item.factura?.id === value.id
      );
      if (usados?.length > 0) {
        const textoMercaderias = usados
          .map(mercaderia => mercaderia.descripcion)
          .join('\n');
        setConfirmConfig({
          open: true,
          onConfirm: () => {
            setConfirmConfig(prev => ({ ...prev, open: false }));
            onDelete(value);
          },
          title: 'Eliminar factura',
          description: `Esta factura está siendo utilizada por las siguientes mercaderías:\n
          ${textoMercaderias}\n
          Si confirma, deberá seleccionar nuevamente una factura para estas mercaderías`,
          cancelText: 'Cancelar',
          okText: 'Eliminar',
          onCancel: () => setConfirmConfig(prev => ({ ...prev, open: false }))
        });
      } else {
        onDelete(value);
      }
    } else {
      onDelete(value);
    }
  };

  const configuraciones = facturasConfig({
    classes,
    enqueueSnackbar,
    onPdfView,
    onEdit,
    onDelete: onDeletePre
  });
  const dataset = 'facturas';

  const { datosConfig } = configuraciones[dataset];
  const { datosSettings } = configuraciones[dataset];

  const onReloadTable = () => {
    props.datos.list.dataLoaded(formik.values.facturas, {
      currentPage: 0,
      pageSize: 10,
      itemsCount: formik.values.facturas.length
    });
  };

  const onChangeFactura = value => {
    if (value.id) {
      const index = formik.values.facturas.findIndex(
        item => item.id === value.id
      );
      formik.setFieldValue(`facturas[${index}]`, value);
      props.datos.list.resetListData();
      const newArray = [...formik.values.facturas];
      newArray[index] = value;
      props.datos.list.dataLoaded(newArray, {
        currentPage: 0,
        pageSize: 10,
        itemsCount: formik.values.facturas.length
      });

      // Se actualizan la mercaderías que usaban esa factura
      const newArrayMercaderias = [...formik.values.mercaderias];
      const mercaderias = newArrayMercaderias.map(item => {
        if (item.factura.id === value.id) {
          return {
            ...item,
            factura: {
              ...value,
              label: `${value.nroFactura} - ${formatNumber(value.montoFactura)}`
            }
          };
        }
        return item;
      });
      formik.setFieldValue(`mercaderias`, mercaderias);
    } else {
      const newData = { id: -(formik.values.facturas.length + 1), ...value };
      formik.setFieldValue(`facturas`, [
        ...(formik.values.facturas || []),
        newData
      ]);
      props.datos.list.dataLoaded([newData], {
        currentPage: 0,
        pageSize: 10,
        itemsCount: formik.values.facturas.length + 1
      });
    }
  };

  const onChangeFacturaPre = value => {
    if (value.id) {
      const usados = formik.values?.mercaderias?.filter(
        item => item.factura?.id === value.id
      );
      if (usados?.length > 0) {
        const textoMercaderias = usados
          .map(mercaderia => mercaderia.descripcion)
          .join('\n');
        setConfirmConfig({
          open: true,
          onConfirm: () => {
            setConfirmConfig(prev => ({ ...prev, open: false }));
            onChangeFactura(value);
          },
          title: 'Editar factura',
          description: `Esta factura está siendo utilizada por las siguientes mercaderías:\n
          ${textoMercaderias}`,
          cancelText: 'Cancelar',
          okText: 'OK',
          onCancel: () => setConfirmConfig(prev => ({ ...prev, open: false }))
        });
      } else {
        onChangeFactura(value);
      }
    } else {
      onChangeFactura(value);
    }
  };

  useEffect(() => {
    const obtenerMondedas = async () => {
      const response = await MonedaService.getAll();
      if (response.data && response.data.data) {
        setMonedas(response.data.data);
      }
    };
    obtenerMondedas();
  }, []);

  return (
    <>
      <Grid container item>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Formulario para carga de Facturas
        </Typography>
      </Grid>
      <Grid container {...gridProps}>
        <Grid item xs={12} md={6}>
          <AutocompleteWithFormik
            required
            label="Seleccione la moneda"
            name="moneda"
            options={monedas}
            labelKey="descripcion"
            valueKey="codigo"
            isObjectValue
            // afterChange={val =>
            //   getTotalDolares(formik.values.mercaderias, val.codigo)
            // }
          />
        </Grid>
        <ConfirmDialog {...facturaConfirmConfig} />
        <FacturasAddForm
          onChange={item => onChangeFacturaPre(item)}
          config={facturaConfig}
          onClose={() => setFacturaConfig({ ...facturaConfig, open: false })}
          gridProps={gridProps}
        />
        <Grid container item spacing={1} className={classes.tableContainerItem}>
          <Grid item>
            <ListDataProvider
              listStore={props.datos.list}
              render={listProps => {
                const newListProps = listProps;
                if (newListProps.dataTableState) {
                  delete newListProps.dataTableState.pagerData;
                }
                return (
                  <DataTable
                    title={datosConfig.titulo}
                    disableReload
                    settings={datosSettings}
                    onReloadTable={() => onReloadTable()}
                    fileName={datosConfig.servicio}
                    error={formik.errors.facturas && formik.touched.facturas}
                    toolbarActions={[
                      <Button
                        variant="outlined"
                        key="agregar"
                        color="primary"
                        className={classes.button}
                        startIcon={<Add />}
                        onClick={() =>
                          setFacturaConfig({
                            defaultValue: facturaDefault,
                            open: true
                          })
                        }
                      >
                        Agregar
                      </Button>
                    ]}
                    {...listProps}
                  />
                );
              }}
            />
          </Grid>
        </Grid>
        <ErrorMessage
          name="facturas"
          render={msg => <FormHelperText error>{t(msg)}</FormHelperText>}
        />
        {pdfPreview && (
          <DocumentPreview
            title={pdfPreview.title}
            file={pdfPreview.url}
            onClose={() => {
              setPdfPreview();
            }}
          />
        )}
      </Grid>
    </>
  );
};

export default inject('datos')(observer(withSnackbar(FacturasForm)));
